import React, { useEffect, useState } from 'react';

const buildRows = () =>
  Array.from({ length: 5 }, () => ({
    selected: true,
    title: '',
    quantity: 1,
    price: '',
  }));

export default function ManualTagsModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  tagPreset,
  setTagPreset,
}) {
  const [rows, setRows] = useState(buildRows());
  const [allSelected, setAllSelected] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setRows(buildRows());
      setAllSelected(true);
    }
  }, [isOpen]);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const toggleAll = (checked) => {
    setAllSelected(checked);
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  };

  useEffect(() => {
    const selectedCount = rows.filter((row) => row.selected).length;
    if (rows.length > 0) {
      setAllSelected(selectedCount === rows.length);
    }
  }, [rows]);

  const handleSubmit = () => {
    onSubmit?.(rows);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-xl font-bold">Manual Tags</h2>
          <button onClick={onClose} className="text-3xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-600">
            Enter quick tag details below. Only rows with a title will be
            created and printed.
          </p>

          <div className="mt-4 flex items-center gap-2">
            <label htmlFor="manual-tag-preset" className="text-sm font-semibold text-gray-700">
              Printer preset:
            </label>
            <select
              id="manual-tag-preset"
              value={tagPreset}
              onChange={(e) => setTagPreset?.(e.target.value)}
              className="h-9 text-sm bg-white text-gray-900 border border-gray-300 rounded-md px-3 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="" disabled>
                Select Price Tag Export
              </option>
              <optgroup label="Phomemo M110 (Scucci’s Choice)">
                <option value="PHOMEMO_40x30">40 mm × 30 mm PDF</option>
                <option value="PHOMEMO_30x20">30 mm × 20 mm PDF</option>
              </optgroup>
              <optgroup label="CSV Generator">
                <option value="BROTHER_CSV">Brother</option>
                <option value="DYMO_CSV">DYMO</option>
                <option value="ZEBRA_CSV">Zebra</option>
                <option value="ROLLO_CSV">Rollo</option>
              </optgroup>
              <optgroup label="Standard Printer">
                <option value="AVERY_5160_QR">Avery 5160 / 8160 (QR)</option>
                <option value="AVERY_5160">Avery 5160 / 8160</option>
                <option value="AVERY_5167">Avery 5167 / 8167</option>
                <option value="AVERY_94102">Avery 94102 (3/4" Square)</option>
                <option value="PLS275">PLS275 1" × 0.5"</option>
              </optgroup>
            </select>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="text-gray-600">
                  <th className="border-b px-2 py-2 font-semibold w-12">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Select all manual tag rows"
                    />
                  </th>
                  <th className="border-b px-2 py-2 font-semibold">Title</th>
                  <th className="border-b px-2 py-2 font-semibold w-24">Qty</th>
                  <th className="border-b px-2 py-2 font-semibold w-32">Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-b last:border-b-0">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={(e) =>
                          updateRow(idx, 'selected', e.target.checked)
                        }
                        aria-label={`Select row ${idx + 1}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={row.title}
                        onChange={(e) =>
                          updateRow(idx, 'title', e.target.value)
                        }
                        placeholder="Enter title here"
                        className="w-full rounded-md border border-gray-300 px-2 py-1 text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min="1"
                        value={row.quantity}
                        onChange={(e) =>
                          updateRow(idx, 'quantity', e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-2 py-1 text-gray-800 focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.price}
                        onChange={(e) => updateRow(idx, 'price', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1 text-gray-800 focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t p-4">
          <button
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Create & Print
          </button>
        </div>
      </div>
    </div>
  );
}
